/**
 * stagedSendTileGate — which messages render as the LIVE staged-send tile, and
 * the one switch that turns it off (2026-09-17).
 *
 * Its own module, and Firebase-free, for the same reason `authorizeCardHtml` is:
 * this predicate is read inside the transcript render path, which must not grow
 * an import graph, and a test must be able to exercise the rule without
 * mounting a chat.
 *
 * ABSENT ⇒ OFF. The React tile is new; while `REACT_APP_STAGED_SEND_EDIT` is
 * unset, every staged-send card renders exactly the html it rendered before the
 * tile existed. Flipping the variable is the whole rollout, and flipping it back
 * is the whole rollback — the html lane is never removed, and the tile itself
 * falls back to that same html whenever its listener cannot serve.
 */

/** The payload kind the batch tile has posted since it was built. */
export const STAGED_SEND_PAYLOAD_KIND = 'prest0n.batch_send_offer';

/** Reads the flag each call rather than caching it, so a test can flip it and
 *  so a bundle that ships with it off never memoizes a stale `true`. */
export function stagedSendTileEnabled(): boolean {
  try {
    return String(process.env.REACT_APP_STAGED_SEND_EDIT || '').toLowerCase() === 'on';
  } catch {
    return false;
  }
}

/**
 * The batch id a message's payload names, or null when this message is not a
 * staged-send card (or the lever is off). Deliberately strict: a payload whose
 * `batchId` is missing or not a non-empty string is NOT a staged-send card, and
 * falling through to the html lane is always safe.
 */
export function stagedSendBatchIdOf(message: unknown): string | null {
  if (!stagedSendTileEnabled()) return null;
  const payload = (message as { payload?: { kind?: unknown; data?: { batchId?: unknown } } } | null)?.payload;
  if (!payload || payload.kind !== STAGED_SEND_PAYLOAD_KIND) return null;
  const batchId = payload.data?.batchId;
  if (typeof batchId !== 'string' || !batchId.trim()) return null;
  return batchId.trim();
}
